import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
  SetStateAction,
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { useParams } from 'react-router-dom'
import { RouteParams } from '@sketch/modules-common'
import {
  PresentationManifest,
  PresentationManifestArtboard,
  PresentationManifestPrototypeStructure,
  PrototypeResizeMode,
  isDefinedPresentationManifestArtboard,
  usePrototypeEvent,
} from '@sketch-hq/sketch-web-renderer'
import { fullscreenApi } from '@sketch/utils'

/**
 * Context for values and methods that are global to components within the
 * prototype view.
 */
type PrototypeContext = {
  /**
   * The Artboard UUID that should be passed to the player when it boots, to be
   * used to populate the initial item in the player's Screen nav stack.
   */
  initialArtboardUUID: string
  /**
   * The UUID that represents the current Prototype flow being played. This is
   * the Artboard UUID of that flow's start point. This UUID is used when
   * restarting the Prototype.
   */
  prototypeArtboardUUID: string
  /**
   * Do the Artboard ids in the url map to real Artboards in the prototype manifest?
   */
  isValid: boolean
  /**
   * The manifest.json file for the prototype's document presentation assets.
   */
  manifest: PresentationManifest | null
  /**
   * The prototype structure data - a flat map of all Artboards appearing in the
   * Prototype. This data is passed to the renderer during its initialization,
   * and is used to create an overview of the document's prototypes.
   */
  prototypeStructure: PresentationManifestPrototypeStructure | null
  /**
   * Helper function to retrieve an Artboard from the prototype structure.
   */
  getArtboard: (id: string) => PresentationManifestArtboard | undefined
  /**
   * Setter for the manifest.json.
   */
  setManifest: Dispatch<SetStateAction<PresentationManifest | null>>
  /**
   * The resize mode for the Prototype renderer.
   */
  resizeMode: PrototypeResizeMode
  /**
   * Setter for the resize mode.
   */
  setResizeMode: Dispatch<SetStateAction<PrototypeResizeMode>>
  /**
   * Whether fullscreen mode is active.
   */
  isFullscreen: boolean
  /**
   * Enter fullscreen mode.
   */
  enterFullscreen: () => void
  /**
   * Exit fullscreen mode.
   */
  exitFullscreen: () => void
  /**
   * Whether the exit fullscreen button should be shown.
   */
  showExitFullscreenButton: boolean
  /**
   * Hide the exit fullscreen button.
   */
  toggleOffExitFullscreenButton: () => void
}

const context = createContext<PrototypeContext | undefined>(undefined)

type PrototypeContextProviderProps = {
  children: ReactNode
}

export function PrototypeContextProvider({
  children,
}: PrototypeContextProviderProps) {
  const [manifest, setManifest] = useState<PresentationManifest | null>(null)
  const [resizeMode, setResizeMode] = useState<PrototypeResizeMode>(
    PrototypeResizeMode.Fit
  )

  const initialArtboardUUID = useInitialArtboardUUID()
  const prototypeArtboardUUID = usePrototypeArtboardUUID()
  const fullscreen = useFullscreen()
  const prototypeStructure = usePrototypeStructure(manifest)

  const getArtboard = useCallback(
    (id: string) =>
      prototypeStructure?.artboards?.find(artboard => artboard.id === id),
    [prototypeStructure]
  )

  const isValid =
    !!getArtboard(initialArtboardUUID) && !!getArtboard(prototypeArtboardUUID)

  const value = useMemo(() => {
    return {
      manifest,
      setManifest,
      resizeMode,
      setResizeMode,
      initialArtboardUUID,
      prototypeArtboardUUID,
      prototypeStructure,
      isValid,
      getArtboard,
      ...fullscreen,
    } as const
  }, [
    fullscreen,
    getArtboard,
    initialArtboardUUID,
    isValid,
    manifest,
    prototypeStructure,
    resizeMode,
    prototypeArtboardUUID,
  ])

  return <context.Provider value={value}>{children}</context.Provider>
}

export function usePrototypeContext() {
  const contextValue = useContext(context)

  if (!contextValue) {
    throw new Error(
      'usePrototypeContext must be used within a PrototypeContextProvider'
    )
  }

  return contextValue
}

/**
 * Determine the initial Artboard UUID passed to the renderer when it boots.
 * Counterintuitively the `currentArtboardUUID` route param is used here - this
 * is what enables deep linking to a specific Artboard within a Prototype.
 */
function useInitialArtboardUUID() {
  const { currentArtboardUUID } = useParams<
    RouteParams<'PROTOTYPE_VIEW_NEXT'>
  >()
  // Save this as a ref because we don't use the route param as a reactive/dynamic
  // value, the source of truth for the current Artboard is in the player
  const currentArtboardUUIDRef = useRef(currentArtboardUUID)
  return currentArtboardUUIDRef.current
}

function usePrototypeArtboardUUID() {
  const { prototypeArtboardUUID } = useParams<
    RouteParams<'PROTOTYPE_VIEW_NEXT'>
  >()
  // We're happy to allow this value to change if/when the route is updated
  return prototypeArtboardUUID
}

function usePrototypeStructure(manifest: PresentationManifest | null) {
  return useMemo(() => {
    if (!manifest) return null
    const prototypeStructure: PresentationManifestPrototypeStructure = {
      artboards: manifest.contents.pages
        .flatMap(page => page.artboards)
        .filter(isDefinedPresentationManifestArtboard),
    }
    return prototypeStructure
  }, [manifest])
}

/**
 * Context logic related to entering and exiting fullscreen mode on the
 * Prototype. We still want to support a fullscreen mode even for browsers that
 * don't support the native fullscreen API, and for these browsers we just
 * visually hide the Prototype header.
 */
function useFullscreen() {
  /**
   * Whether fullscreen mode is active. This is true when either the native
   * fullscreen API is active *or* we've just hidden the header.
   */
  const [isFullscreen, setIsFullscreen] = useState(
    () => fullscreenApi.fullscreenElement() !== null
  )

  /**
   * Whether the exit fullscreen button has been visually toggled on or off.
   * Browsers with a native fullscreen API provide their own UI/UX for exiting
   * fullscreen mode, so in this case we never want to show an exit fullscreen
   * button. For browsers that don't support native fullscreen we'll only toggle
   * the exit fullscreen button on when a user clicks outside a hotspot (the so-
   * called "unhandled interaction" on the Prototype).
   */
  const [
    isExitFullscreenButtonToggled,
    setIsExitFullscreenButtonToggled,
  ] = useState(false)

  const enterFullscreen = useCallback(async () => {
    try {
      await fullscreenApi.requestFullscreen(document.documentElement, {
        navigationUI: 'hide',
      })
    } catch (e) {
      // An error here means the browser doesn't support native fullscreen.
      // We can ignore it however, and set `isFullscreen` to `true` regardless
      // since we still want to hide the header
    }
    setIsFullscreen(true)
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      await fullscreenApi.exitFullscreen()
    } catch (e) {
      // Ignore for same reason as above
    }
    setIsFullscreen(false)
  }, [])

  // User can enter and exit fullscreen mode without calling our functions by
  // using native browser buttons or shortcuts - so we still need to detect
  // this happening and sync our `active` value
  useEffect(() => {
    function onFullscreenChanged() {
      setIsFullscreen(Boolean(fullscreenApi.fullscreenElement()))
    }

    if (fullscreenApi.changeEvent) {
      document.addEventListener(fullscreenApi.changeEvent, onFullscreenChanged)
    }

    return () => {
      if (fullscreenApi.changeEvent) {
        document.removeEventListener(
          fullscreenApi.changeEvent,
          onFullscreenChanged
        )
      }
    }
  }, [])

  const onUnhandledClickInteraction = useCallback(() => {
    if (isFullscreen) {
      setIsExitFullscreenButtonToggled(value => !value)
    }
  }, [isFullscreen])

  usePrototypeEvent('prototypeUnhandledPointerUp', onUnhandledClickInteraction)

  const toggleOffExitFullscreenButton = useCallback(() => {
    setIsExitFullscreenButtonToggled(false)
  }, [])

  // Show the exit fullscreen button if...
  const showExitFullscreenButton =
    isFullscreen && // ...we're in fullscreen mode
    fullscreenApi.fullscreenElement() === null && // ... and the browser doesn't have a native fullscreen UX
    isExitFullscreenButtonToggled // ... and we've visually toggled on the button (i.e. user clicked outside a hotspot)

  return useMemo(() => {
    return {
      isFullscreen,
      enterFullscreen,
      exitFullscreen,
      showExitFullscreenButton,
      toggleOffExitFullscreenButton,
    } as const
  }, [
    enterFullscreen,
    exitFullscreen,
    isFullscreen,
    showExitFullscreenButton,
    toggleOffExitFullscreenButton,
  ])
}
